import { useCallback, useState } from "react";
import type { WalkthroughClipResponse } from "@patent-studio/api-types";
import type { Definitions } from "@/lib/element-registry";
import type { DefinitionChange } from "./use-figure-definitions";

/** Per drawing, the change that took its walk-through's confirmation off, until it is confirmed again. */
export type Lifted = ReadonlyMap<string, DefinitionChange>;

/**
 * The drawings whose confirmed walk-through a change takes the confirmation off,
 * other than by the attorney's own amend: the ones the screen owes a reason.
 */
export function confirmationsLifted(
  before: Definitions,
  after: Definitions,
  change: DefinitionChange,
): string[] {
  if (change.kind === "walkthrough-accepted") return [];
  const still = new Set(
    after.figures
      .filter((figure) => figure.walkthrough_accepted)
      .map((figure) => figure.file_id),
  );
  return before.figures
    .filter(
      (figure) => figure.walkthrough_accepted && !still.has(figure.file_id),
    )
    .map((figure) => figure.file_id);
}

function liftedBy(
  held: Lifted,
  change: DefinitionChange,
  before: Definitions,
  after: Definitions,
): Lifted {
  const lifts = confirmationsLifted(before, after, change);
  const answered =
    change.kind === "walkthrough-accepted" && held.has(change.fileId);
  if (lifts.length === 0 && !answered) return held;
  const next = new Map(held);
  if (answered) next.delete(change.fileId);
  for (const fileId of lifts) next.set(fileId, change);
  return next;
}

/**
 * Which drawings lost their walk-through confirmation to a change other than Amend.
 *
 * Each change is noted against the set it applies to, folded by `fold`, so a
 * lift is seen the moment it is asked for. Confirming the drawing again clears it.
 */
export function useLiftedConfirmations(
  fold: (change: DefinitionChange, definitions: Definitions) => Definitions,
) {
  const [lifted, setLifted] = useState<Lifted>(() => new Map());
  const note = useCallback(
    (change: DefinitionChange, before: Definitions) => {
      setLifted((held) => liftedBy(held, change, before, fold(change, before)));
    },
    [fold],
  );
  return [lifted, note] as const;
}

/** What took a walk-through's confirmation off, in the attorney's terms; null when nothing did. */
export function liftReason(
  change: DefinitionChange | undefined,
  clips: readonly WalkthroughClipResponse[],
): string | null {
  if (change === undefined) return null;
  if (change.kind === "walkthrough") return "the words were edited";
  if (change.kind === "adopt-transcript") {
    const clip = clips.find((held) => held.file_id === change.clipId);
    return `the transcript of ${clip?.filename ?? "a clip"} was added`;
  }
  return "a later change rewrote it";
}
